import React from 'react';
import { Link } from 'react-router-dom';
import { HardHat, ChevronRight, FileText, Smartphone, TrendingUp, Star, Bot } from 'lucide-react';

// --- Page Component ---
const LandingPage = () => {
  const colors = {
    nightBlue: '#1a2b4b',
    safetyOrange: '#ff9800',
  };

  return (
    <div className="min-h-screen bg-white font-sans text-slate-800">
      <Header colors={colors} />
      <main>
        <HeroSection colors={colors} />
        <ClientsSection colors={colors} />
        <FeaturesSection colors={colors} />
        <TestimonialSection colors={colors} />
        <CtaSection colors={colors} />
      </main>
      <Footer colors={colors} />
    </div>
  );
};

// --- Sub-components for each section ---

const Header = ({ colors }) => (
  <header className="bg-white/80 backdrop-blur-md sticky top-0 z-50 border-b border-slate-200">
    <div className="container mx-auto px-6 py-4 flex justify-between items-center">
      <Link to="/" className="flex items-center gap-2">
        <div style={{ backgroundColor: colors.nightBlue }} className="w-10 h-10 rounded-lg flex items-center justify-center">
          <HardHat style={{ color: colors.safetyOrange }} size={24} />
        </div>
        <span className="font-bold text-xl" style={{ color: colors.nightBlue }}>Ovrio</span>
      </Link>
      <div className="flex items-center gap-4">
        <Link to="/login" className="font-semibold py-2 px-5 rounded-lg transition-colors text-sm" style={{ color: colors.nightBlue }}>
          Se connecter
        </Link>
        <Link to="/subscribe">
          <button className="hidden sm:block text-white font-bold text-sm py-2 px-5 rounded-lg shadow-md transition-transform hover:scale-105" style={{ backgroundColor: colors.safetyOrange }}>
            Essai Gratuit
          </button>
        </Link>
      </div>
    </div>
  </header>
);

const HeroSection = ({ colors }) => (
  <section className="text-center py-20 md:py-32 px-6 bg-slate-50">
    <div className="container mx-auto">
      <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mx-auto max-w-3xl" style={{ color: colors.nightBlue }}>
        Le logiciel de facturation conçu pour les vrais pros du BTP.
      </h1>
      <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">
        Devis, factures, suivi de chantier : Gagnez du temps sur le terrain et simplifiez votre administratif. Conforme et 100% mobile.
      </p>
      <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/subscribe">
          <button className="w-full sm:w-auto flex items-center justify-center gap-2 text-white font-bold text-lg py-4 px-8 rounded-lg shadow-lg transform hover:scale-105 transition-transform" style={{ backgroundColor: colors.safetyOrange }}>
            ESSAYER GRATUITEMENT
            <ChevronRight size={20} />
          </button>
        </Link>
        <Link to="/login">
          <button className="w-full sm:w-auto font-bold text-lg py-4 px-8 rounded-lg border-2 transition-colors hover:bg-slate-100" style={{ color: colors.nightBlue, borderColor: colors.nightBlue }}>
            Demander une démo
          </button>
        </Link>
      </div>
    </div>
  </section>
);

const ClientsSection = ({ colors }) => (
  <div className="py-16">
    <div className="container mx-auto px-6 text-center">
      <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: colors.nightBlue }}>ILS NOUS FONT CONFIANCE SUR LE CHANTIER</h3>
      <div className="flex flex-wrap justify-center items-center gap-x-8 md:gap-x-12 gap-y-4 mt-8 opacity-50">
        <div className="h-8 w-28 bg-slate-300 rounded-md animate-pulse"></div>
        <div className="h-8 w-32 bg-slate-300 rounded-md animate-pulse"></div>
        <div className="h-8 w-24 bg-slate-300 rounded-md animate-pulse"></div>
        <div className="h-8 w-36 bg-slate-300 rounded-md animate-pulse"></div>
        <div className="h-8 w-28 bg-slate-300 rounded-md animate-pulse"></div>
      </div>
    </div>
  </div>
);

const Feature = ({ icon: Icon, title, children, imageSide = 'right' }) => (
  <div className="container mx-auto px-6 py-16">
    <div className={`flex flex-col md:flex-row items-center justify-between gap-12 ${imageSide === 'left' ? 'md:flex-row-reverse' : ''}`}>
      <div className="md:w-1/2 text-center md:text-left">
        <div className="inline-flex items-center gap-3 bg-orange-100/50 rounded-full py-2 px-4 mb-4">
          <Icon className="text-orange-500" size={20} />
          <h3 className="font-bold text-orange-800">{title}</h3>
        </div>
        <p className="text-3xl font-bold text-slate-800 leading-snug">
          {children}
        </p>
      </div>
      <div className="md:w-5/12">
        <div className="w-full h-64 bg-slate-200 rounded-2xl shadow-md animate-pulse"></div>
      </div>
    </div>
  </div>
);

const FeaturesSection = () => (
  <section className="bg-slate-50">
    <Feature icon={FileText} title="Devis & Factures" imageSide="right">
      Créez des devis et factures professionnels en quelques minutes, directement depuis votre téléphone ou tablette.
    </Feature>
    <Feature icon={Smartphone} title="Suivi de Chantier" imageSide="left">
      Centralisez vos documents, photos et notes par chantier. Toute votre équipe reste synchronisée, même à distance.
    </Feature>
    <Feature icon={Bot} title="Assistant IA" imageSide="right">
      Laissez notre IA générer des descriptions de travaux précises et professionnelles pour vos devis. Gagnez en temps et en crédibilité.
    </Feature>
  </section>
);

const TestimonialSection = ({ colors }) => (
  <section className="py-24">
    <div className="container mx-auto px-6 text-center max-w-3xl">
      <div className="flex justify-center text-yellow-400 mb-4 gap-1">
        <Star /><Star /><Star /><Star /><Star />
      </div>
      <p className="text-2xl md:text-3xl font-medium leading-relaxed" style={{ color: colors.nightBlue }}>
        “Ovrio a changé ma façon de travailler. Je gagne au moins une heure par jour sur l'administratif. C'est simple, rapide, et mes clients adorent les devis clairs.”
      </p>
      <p className="mt-6 font-bold text-slate-800">Julien P. - Artisan Plombier</p>
      <p className="text-sm text-slate-500">Client Ovrio depuis 2024</p>
    </div>
  </section>
);

const CtaSection = ({ colors }) => (
  <section className="bg-slate-50 py-20">
    <div className="container mx-auto px-6 text-center">
      <h2 className="text-3xl md:text-4xl font-extrabold" style={{ color: colors.nightBlue }}>
        Prêt à simplifier votre quotidien ?
      </h2>
      <p className="mt-4 text-lg text-slate-600 max-w-xl mx-auto">
        Démarrez votre essai gratuit de 14 jours. Pas de carte de crédit requise.
      </p>
      <div className="mt-8">
        <Link to="/subscribe">
            <button className="flex items-center justify-center gap-2 text-white font-bold text-lg py-4 px-8 rounded-lg shadow-lg transform hover:scale-105 transition-transform mx-auto" style={{ backgroundColor: colors.safetyOrange }}>
              Démarrer mon essai gratuit
              <ChevronRight size={20} />
            </button>
        </Link>
      </div>
    </div>
  </section>
);

const Footer = ({ colors }) => (
  <footer className="py-8" style={{ backgroundColor: colors.nightBlue }}>
    <div className="container mx-auto px-6 text-center text-slate-400 text-sm">
      <p>© 2025 Ovrio. Tous droits réservés.</p>
      <div className="flex justify-center gap-6 mt-4">
        <Link to="/legal" className="hover:text-white transition">Mentions Légales</Link>
        <Link to="/cgu" className="hover:text-white transition">Conditions d'Utilisation</Link>
      </div>
    </div>
  </footer>
);

export default LandingPage;